import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { fetchUserById } from '@src/main/modules/auth/application/fetchUserById';
import { AuthContextType, User } from '@src/main/modules/user/domain/user.interface';
import { logger } from '@src/main/shared/utils/logger';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface IdTokenClaims {
  employeeid: string;
  [key: string]: unknown;
}

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [user, setUser] = useState<User | null>(null);
  const [msalReady, setMsalReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize();
        setMsalReady(true);
      } catch (error) {
        logger.error('MSAL initialization error:', error);
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, [instance]);

  useEffect(() => {
    if (!msalReady || !account) {
      if (!account && msalReady) {
        setIsLoading(false);
      }
      return;
    }

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
        const employeeId = (account.idTokenClaims as IdTokenClaims)?.employeeid.toLowerCase();
        const email = (account.idTokenClaims as IdTokenClaims)?.email as string;
        const userFromApi = await fetchUserById(tokenResponse.idToken, employeeId);

        const userData: User = {
          ...userFromApi,
          token: tokenResponse.idToken,
          email,
        };
        setUser(userData);
      } catch (error) {
        logger.error('Error during authentication:', error);

        if (error instanceof InteractionRequiredAuthError) {
          instance.loginRedirect();
          return;
        }

        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [account, accounts, instance, msalReady]);

  const hasPermission = useCallback(
    (permission: string) => user?.permissions?.includes(permission) ?? false,
    [user],
  );

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      setUser,
      hasPermission,
      logout: () => instance.logoutRedirect(),
    }),
    [user, isLoading, setUser, hasPermission, instance],
  );

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
